/**
 * ADMIN USER MANAGER
 * 
 * Component for managing admin users in the multi-admin RBAC system.
 * Allows super admins to create, edit, and deactivate admin accounts.
 */

import React, { useState, useEffect } from 'react'
import { getAllRoles, getRolePermissions } from '../../lib/adminPermissions'
import { logAdminAction } from '../../lib/adminAuth'
import { getSessionHeaders } from '../../lib/serverSession';
import { logAction } from '../../lib/personalActivityLogger';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface AdminUser {
  id: string
  email: string
  full_name?: string
  phone?: string
  role: string
  is_active: boolean
  created_at: string
  last_login_at?: string
  notes?: string
}

interface Role {
  id: string
  role_name: string
  display_name: string
  description: string
}

interface AdminUserManagerProps {
  currentUser: any
}

export default function AdminUserManager({ currentUser }: AdminUserManagerProps) {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [testUsers, setTestUsers] = useState<Array<{
    id: number
    username: string | null
    email: string | null
    full_name: string | null
    account_status: string | null
    visibility: string
  }>>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [rolePermissions, setRolePermissions] = useState<string[]>([])
  const [purgeUserId, setPurgeUserId] = useState<number | null>(null)
  const [purgeConfirm, setPurgeConfirm] = useState('')
  const [purgeBusy, setPurgeBusy] = useState(false)
  const [purgePlan, setPurgePlan] = useState<any>(null)
  const [purgeMessage, setPurgeMessage] = useState('')

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    role: 'admin',
    notes: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions(selectedRole)
    }
  }, [selectedRole])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load admin users via API
      const response = await fetch(`${API_BASE_URL}/api/admin/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_admin_users' })
      })
      const result = await response.json()

      if (!response.ok) {
        console.error('Error loading admin users:', result.error)
      } else {
        setAdminUsers(result.data?.users || [])
      }

      const testResponse = await fetch(`${API_BASE_URL}/api/admin/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_test_users' })
      })
      const testResult = await testResponse.json()
      if (testResponse.ok) {
        setTestUsers(testResult.data?.users || [])
      }

      // Load roles
      const rolesData = await getAllRoles()
      setRoles(rolesData)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const runTestUserPurge = async (userId: number, apply: boolean) => {
    try {
      setPurgeBusy(true)
      setPurgeMessage('')
      const response = await fetch(`${API_BASE_URL}/api/admin/purge-admin-test-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          confirm: apply ? purgeConfirm : undefined,
          apply,
        })
      })
      const result = await response.json()
      if (!response.ok) {
        setPurgeMessage(result.error || 'Purge failed')
        return
      }
      setPurgePlan(result.plan || null)
      if (apply) {
        setPurgeMessage(`Purged test rows after 2 Nov 2026 00:00 SAST. Kept ${result.result?.keptPurchases || 0} older purchases. Live upline commissions were not clawed.`)
        setPurgeConfirm('')
        await loadData()
      }
    } catch (error) {
      setPurgeMessage(error instanceof Error ? error.message : 'Purge failed')
    } finally {
      setPurgeBusy(false)
    }
  }

  const loadRolePermissions = async (roleName: string) => {
    const permissions = await getRolePermissions(roleName)
    setRolePermissions(permissions)
  }

  const handleCreateAdmin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'create_admin',
          data: {
            email: formData.email,
            full_name: formData.full_name,
            phone: formData.phone,
            role: formData.role,
            notes: formData.notes,
            created_by: currentUser?.adminUser?.id
          }
        })
      })
      const result = await response.json()

      if (!response.ok) {
        alert(`Error creating admin user: ${result.error}`)
        return
      }

      const newAdmin = result.data?.admin

      // Log the action
      await logAdminAction(
        currentUser?.email,
        'create_admin_user',
        'admin_user',
        newAdmin?.id,
        { email: formData.email, role: formData.role }
      )

      logAction('admin_user' as any, 'admin_user_created', { target_type: 'admin_user', target_id: String(newAdmin?.id || ''), target_label: formData.email, details: { role: formData.role } })

      alert('✅ Admin user created successfully!\n\nThe new admin will receive a PIN code via email when they attempt to log in.')

      setShowCreateModal(false)
      resetForm()
      loadData()
    } catch (error) {
      console.error('Error creating admin:', error)
      alert('Failed to create admin user')
    }
  }

  const handleUpdateAdmin = async () => {
    if (!editingUser) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update_admin',
          data: {
            adminId: editingUser.id,
            full_name: formData.full_name,
            phone: formData.phone,
            role: formData.role,
            notes: formData.notes
          }
        })
      })
      const result = await response.json()

      if (!response.ok) {
        alert(`Error updating admin user: ${result.error}`)
        return
      }

      // Log the action
      await logAdminAction(
        currentUser?.email,
        'update_admin_user',
        'admin_user',
        editingUser.id,
        {
          old_role: editingUser.role,
          new_role: formData.role
        }
      )

      logAction('admin_user' as any, 'admin_user_updated', { target_type: 'admin_user', target_id: String(editingUser.id), target_label: editingUser.email, details: { old_role: editingUser.role, new_role: formData.role } })

      alert('✅ Admin user updated successfully!')

      setEditingUser(null)
      resetForm()
      loadData()
    } catch (error) {
      console.error('Error updating admin:', error)
      alert('Failed to update admin user')
    }
  }

  const handleToggleActive = async (user: AdminUser) => {
    if (!confirm(`Are you sure you want to ${user.is_active ? 'deactivate' : 'activate'} ${user.email}?`)) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'toggle_admin_status',
          data: {
            adminId: user.id,
            is_active: !user.is_active
          }
        })
      })
      const result = await response.json()

      if (!response.ok) {
        alert(`Error updating admin status: ${result.error}`)
        return
      }

      // Log the action
      await logAdminAction(
        currentUser?.email,
        user.is_active ? 'deactivate_admin_user' : 'activate_admin_user',
        'admin_user',
        user.id,
        { email: user.email }
      )

      logAction('admin_user' as any, user.is_active ? 'admin_user_deactivated' : 'admin_user_activated', { target_type: 'admin_user', target_id: String(user.id), target_label: user.email })

      loadData()
    } catch (error) {
      console.error('Error toggling admin status:', error)
      alert('Failed to update admin status')
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      role: 'admin',
      notes: ''
    })
    setSelectedRole('')
    setRolePermissions([])
  }

  const openEditModal = (user: AdminUser) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      full_name: user.full_name || '',
      phone: user.phone || '',
      role: user.role,
      notes: user.notes || ''
    })
    setSelectedRole(user.role)
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ color: '#ffffff', fontSize: '18px' }}>Loading admin users...</div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          color: '#FFFFFF',
          margin: 0
        }}>
          👥 Admin User Management
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            backgroundColor: '#10B981',
            color: '#FFFFFF',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          ➕ Create New Admin
        </button>
      </div>

      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.5)',
        borderRadius: '12px',
        border: '1px solid #92400E',
        overflow: 'hidden',
        marginBottom: '24px'
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #374151' }}>
          <h3 style={{ margin: 0, color: '#FBBF24', fontSize: '16px', fontWeight: 600 }}>
            Admin Test Users
          </h3>
          <p style={{ margin: '8px 0 0', color: '#D1D5DB', fontSize: '13px' }}>
            Flagged accounts keep full numbers on their own user dashboard only. They are excluded from the pool write, ticker, shareholder totals, and every public or other-member visual. Purge test data only cancels that user&apos;s rows from 2 Nov 2026 00:00 SAST onward. Older scweb sales stay. Other members and live upline commissions are not written.
          </p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(17, 24, 39, 0.8)' }}>
              <th style={tableHeaderStyle}>User ID</th>
              <th style={tableHeaderStyle}>Username</th>
              <th style={tableHeaderStyle}>Email</th>
              <th style={tableHeaderStyle}>Name</th>
              <th style={tableHeaderStyle}>Visibility</th>
              <th style={tableHeaderStyle}>Test purge</th>
            </tr>
          </thead>
          <tbody>
            {testUsers.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '16px 20px', color: '#9CA3AF' }}>No flagged test users.</td>
              </tr>
            ) : testUsers.map((user) => (
              <tr key={user.id}>
                <td style={tableCellStyle}>{user.id}</td>
                <td style={tableCellStyle}>{user.username || '—'}</td>
                <td style={tableCellStyle}>{user.email || '—'}</td>
                <td style={tableCellStyle}>{user.full_name || '—'}</td>
                <td style={tableCellStyle}>Own dashboard only</td>
                <td style={tableCellStyle}>
                  <button
                    type="button"
                    onClick={() => {
                      setPurgeUserId(user.id)
                      setPurgeConfirm('')
                      setPurgeMessage('')
                      runTestUserPurge(user.id, false)
                    }}
                    disabled={purgeBusy}
                    style={{
                      backgroundColor: '#7F1D1D',
                      color: '#FEE2E2',
                      border: '1px solid #F87171',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: purgeBusy ? 'wait' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    Purge test data
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {purgeUserId != null && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid #374151', backgroundColor: 'rgba(127, 29, 29, 0.2)' }}>
            <p style={{ margin: '0 0 8px', color: '#FECACA', fontSize: '13px' }}>
              Purge user {purgeUserId} test rows from 2 Nov 2026 00:00 SAST onward only. Type PURGE SCWEB TEST.
            </p>
            {purgePlan && (
              <p style={{ margin: '0 0 8px', color: '#E5E7EB', fontSize: '12px' }}>
                Will cancel {purgePlan.purge?.payments || 0} payments and {purgePlan.purge?.purchases || 0} purchases,
                remove {purgePlan.purge?.poolRows || 0} pool rows (${Number(purgePlan.purge?.poolAmount || 0).toFixed(2)}).
                Keep {purgePlan.keep?.payments || 0} older payments and {purgePlan.keep?.purchases || 0} older purchases.
                Live upline commissions stay.
              </p>
            )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder="PURGE SCWEB TEST"
                style={{
                  backgroundColor: '#111827',
                  color: '#F9FAFB',
                  border: '1px solid #F87171',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  fontSize: '13px',
                  minWidth: '220px'
                }}
              />
              <button
                type="button"
                disabled={purgeBusy || purgeConfirm !== 'PURGE SCWEB TEST'}
                onClick={() => runTestUserPurge(purgeUserId, true)}
                style={{
                  backgroundColor: purgeConfirm === 'PURGE SCWEB TEST' ? '#B91C1C' : '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  cursor: purgeBusy || purgeConfirm !== 'PURGE SCWEB TEST' ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '13px'
                }}
              >
                Confirm purge
              </button>
            </div>
            {purgeMessage && (
              <p style={{ margin: '8px 0 0', color: '#FDE68A', fontSize: '12px' }}>{purgeMessage}</p>
            )}
          </div>
        )}
      </div>

      {/* Admin Users Table */}
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.5)',
        borderRadius: '12px',
        border: '1px solid #374151',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(17, 24, 39, 0.8)' }}>
              <th style={tableHeaderStyle}>Email</th>
              <th style={tableHeaderStyle}>Full Name</th>
              <th style={tableHeaderStyle}>Role</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Last Login</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {adminUsers.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #374151' }}>
                <td style={tableCellStyle}>{user.email}</td>
                <td style={tableCellStyle}>{user.full_name || '-'}</td>
                <td style={tableCellStyle}>
                  <span style={{
                    backgroundColor: getRoleBadgeColor(user.role),
                    color: '#FFFFFF',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {getRoleDisplayName(user.role, roles)}
                  </span>
                </td>
                <td style={tableCellStyle}>
                  <span style={{
                    color: user.is_active ? '#10B981' : '#EF4444',
                    fontWeight: '600'
                  }}>
                    {user.is_active ? '✅ Active' : '❌ Inactive'}
                  </span>
                </td>
                <td style={tableCellStyle}>
                  {user.last_login_at 
                    ? new Date(user.last_login_at).toLocaleDateString()
                    : 'Never'}
                </td>
                <td style={tableCellStyle}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => openEditModal(user)}
                      style={actionButtonStyle('#3B82F6')}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(user)}
                      style={actionButtonStyle(user.is_active ? '#EF4444' : '#10B981')}
                      disabled={user.email === currentUser?.email}
                    >
                      {user.is_active ? '🚫 Deactivate' : '✅ Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingUser) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '12px',
            border: '1px solid #374151',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#FFFFFF',
              marginBottom: '24px'
            }}>
              {editingUser ? '✏️ Edit Admin User' : '➕ Create New Admin User'}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Email */}
              <div>
                <label style={labelStyle}>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editingUser}
                  style={inputStyle}
                  placeholder="admin@example.com"
                />
              </div>

              {/* Full Name */}
              <div>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  style={inputStyle}
                  placeholder="John Doe"
                />
              </div>

              {/* Phone */}
              <div>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={inputStyle}
                  placeholder="+1234567890"
                />
              </div>

              {/* Role */}
              <div>
                <label style={labelStyle}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => {
                    setFormData({ ...formData, role: e.target.value })
                    setSelectedRole(e.target.value)
                  }}
                  style={inputStyle}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.role_name}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
                {selectedRole && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px'
                  }}>
                    <div style={{ color: '#60A5FA', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                      Role Permissions ({rolePermissions.length}):
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '14px' }}>
                      {rolePermissions.length > 0
                        ? rolePermissions.join(', ')
                        : 'No permissions assigned'}
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Internal notes about this admin user..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditingUser(null)
                  resetForm()
                }}
                style={{
                  backgroundColor: '#374151',
                  color: '#FFFFFF',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={editingUser ? handleUpdateAdmin : handleCreateAdmin}
                disabled={!formData.email || !formData.role}
                style={{
                  backgroundColor: '#10B981',
                  color: '#FFFFFF',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  opacity: (!formData.email || !formData.role) ? 0.5 : 1
                }}
              >
                {editingUser ? 'Update Admin' : 'Create Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: '600',
  marginBottom: '8px'
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  backgroundColor: '#111827',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#FFFFFF',
  fontSize: '14px',
  outline: 'none'
}

// Helper functions and styles
const tableHeaderStyle: React.CSSProperties = {
  padding: '16px',
  textAlign: 'left',
  color: '#ffffff',
  fontWeight: '600',
  fontSize: '14px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
}

const tableCellStyle: React.CSSProperties = {
  padding: '16px',
  color: '#ffffff',
  fontSize: '14px'
}

const actionButtonStyle = (bgColor: string): React.CSSProperties => ({
  backgroundColor: bgColor,
  color: '#FFFFFF',
  padding: '6px 12px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600'
})

function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'super_admin': return '#8B5CF6'
    case 'admin': return '#3B82F6'
    case 'moderator': return '#10B981'
    case 'support_admin': return '#F59E0B'
    case 'financial_admin': return '#EF4444'
    case 'read_only_admin': return '#6B7280'
    default: return '#6B7280'
  }
}

function getRoleDisplayName(roleName: string, roles: Role[]): string {
  const role = roles.find(r => r.role_name === roleName)
  return role?.display_name || roleName
}

